import React, { useState, useEffect } from 'react';
import Breadcrumb from '../components/layout/Breadcrumb';
import Card from '../components/ui/Card';
import { BarChart3, TrendingUp, PieChart, Activity, Calendar, Recycle, Package, Download } from 'lucide-react';
import OceanWasteChart from '../components/dashboard/charts/OceanWasteChart';
import dashboardAPI from '../services/dashboard';

const Analytics = () => {
  const [oceanWasteData, setOceanWasteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        const stats = await dashboardAPI.getStats();
        setOceanWasteData(stats.oceanWasteBreakdown);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  const [centersData, setCentersData] = useState([]);
  const [wasteAnalyticsData, setWasteAnalyticsData] = useState(null);
  
  // State for filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedWasteType, setSelectedWasteType] = useState('');
  const [selectedWasteName, setSelectedWasteName] = useState('');
  
  useEffect(() => {
    const fetchAdditionalAnalyticsData = async () => {
      try {
        // Fetch centers data with relations
        const centers = await dashboardAPI.getCentersData();
        setCentersData(centers);
        
        // Fetch waste analytics data
        const wasteAnalytics = await dashboardAPI.getWasteAnalytics();
        setWasteAnalyticsData(wasteAnalytics);
      } catch (err) {
        console.error('Error fetching additional analytics data:', err);
      }
    };

    if (!loading && !error) {
      fetchAdditionalAnalyticsData();
    }
  }, [loading, error]);

  // Prepare data for center waste analytics
  const prepareCenterWasteData = () => {
    const data = [];
    
    centersData.forEach(center => {
      const centerName = center.centerName || `Center ${center.id}`;
      
      // Process aggregated wastes
      if (center.aggregatedWastes && center.aggregatedWastes.length > 0) {
        center.aggregatedWastes.forEach(waste => {
          data.push({
            centerName,
            date: waste.date || waste.createdAt || waste.updatedAt,
            typeOfWaste: waste.type || 'Unknown',
            nameOfWaste: waste.name || waste.type || 'Unknown',
            amount: waste.totalAmount || 0
          });
        });
      }
      
      // Process new waste collected
      if (center.newWasteCollecteds && center.newWasteCollecteds.length > 0) {
        center.newWasteCollecteds.forEach(waste => {
          data.push({
            centerName,
            date: waste.date || waste.createdAt || waste.updatedAt,
            typeOfWaste: waste.type || 'Unknown',
            nameOfWaste: waste.name || waste.type || 'Unknown',
            amount: waste.amount || 0
          });
        });
      }
    });
    
    return data;
  };

  // Get unique values for filters
  const centerWasteData = prepareCenterWasteData();
  
  const uniqueCenters = centerWasteData && centerWasteData.length > 0 ? [...new Set(centerWasteData.map(item => item.centerName))] : [];
  const uniqueWasteTypes = centerWasteData && centerWasteData.length > 0 ? [...new Set(centerWasteData.map(item => item.typeOfWaste))] : [];
  const uniqueWasteNames = centerWasteData && centerWasteData.length > 0 ? [...new Set(centerWasteData.map(item => item.nameOfWaste))] : [];
  
  // Apply filters
  const filteredData = centerWasteData.filter(item => {
    const itemDate = new Date(item.date);
    // Skip items with invalid dates
    if (isNaN(itemDate.getTime())) {
      return false;
    }
    
    const fromDateObj = fromDate ? new Date(fromDate) : null;
    const toDateObj = toDate ? new Date(toDate) : null;
    
    const isWithinDateRange = (!fromDateObj || itemDate >= fromDateObj) && 
                              (!toDateObj || itemDate <= toDateObj);
    const isSelectedCenter = !selectedCenter || item.centerName === selectedCenter;
    const isSelectedWasteType = !selectedWasteType || item.typeOfWaste === selectedWasteType;
    const isSelectedWasteName = !selectedWasteName || item.nameOfWaste === selectedWasteName;
    
    return isWithinDateRange && isSelectedCenter && isSelectedWasteType && isSelectedWasteName;
  });
  
  // Prepare data for visualization
  const getChartData = () => {
    const chartData = {};
    
    // Group by date for time series
    const groupedByDate = filteredData.reduce((acc, item) => {
      const date = new Date(item.date);
      if (isNaN(date.getTime())) {
        // Skip invalid dates
        return acc;
      }
      const dateStr = date.toISOString().split('T')[0];
      if (!acc[dateStr]) {
        acc[dateStr] = { date: dateStr, amount: 0 };
      }
      acc[dateStr].amount += item.amount;
      return acc;
    }, {});
    
    chartData.timeSeries = Object.values(groupedByDate);
    
    // Group by center
    const groupedByCenter = filteredData.reduce((acc, item) => {
      if (!acc[item.centerName]) {
        acc[item.centerName] = 0;
      }
      acc[item.centerName] += item.amount;
      return acc;
    }, {});
    
    chartData.byCenter = Object.entries(groupedByCenter).map(([center, amount]) => ({
      name: center,
      amount
    }));
    
    // Group by waste type
    const groupedByType = filteredData.reduce((acc, item) => {
      if (!acc[item.typeOfWaste]) {
        acc[item.typeOfWaste] = 0;
      }
      acc[item.typeOfWaste] += item.amount;
      return acc;
    }, {});
    
    chartData.byType = Object.entries(groupedByType).map(([type, amount]) => ({
      name: type,
      amount
    }));
    
    // Group by waste name
    const groupedByName = filteredData.reduce((acc, item) => {
      if (!acc[item.nameOfWaste]) {
        acc[item.nameOfWaste] = 0;
      }
      acc[item.nameOfWaste] += item.amount;
      return acc;
    }, {});
    
    chartData.byName = Object.entries(groupedByName).map(([name, amount]) => ({
      name: name,
      amount
    }));
    
    return chartData;
  };
  
  const chartData = getChartData();
  
  // Function to download CSV
  const downloadCSV = (data) => {
    if (!data || data.length === 0) {
      return;
    }
    
    // Create CSV content
    const headers = ['Center Name', 'Date', 'Type of Waste', 'Name of Waste', 'Amount (kg)'];
    const csvContent = [
      headers.join(','),
      ...data.map(row => [
        `"${row.centerName}"`,
        row.date ? new Date(row.date).toLocaleDateString() : 'N/A',
        `"${row.typeOfWaste}"`,
        `"${row.nameOfWaste}"`,
        row.amount.toFixed(2)
      ].join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `waste-data-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Function to create pie chart SVG with hover details
  const createPieChart = (data, colors) => {
    if (!data || data.length === 0) return null;
    
    const total = data.reduce((sum, item) => sum + item.amount, 0);
    if (total === 0) return null;
    
    let currentAngle = 0;
    const centerX = 200;
    const centerY = 200;
    const radius = 160;
    
    const paths = data.map((item, index) => {
      const percentage = (item.amount / total) * 100;
      const angle = (item.amount / total) * 2 * Math.PI;
      
      const startX = centerX + radius * Math.cos(currentAngle);
      const startY = centerY + radius * Math.sin(currentAngle);
      
      currentAngle += angle;
      
      const endX = centerX + radius * Math.cos(currentAngle);
      const endY = centerY + radius * Math.sin(currentAngle);
      
      const largeArcFlag = angle > Math.PI ? 1 : 0;
      
      const pathData = [
        'M', centerX, centerY,
        'L', startX, startY,
        'A', radius, radius, 0, largeArcFlag, 1, endX, endY,
        'Z'
      ].join(' ');
      
      return (
        <path
          key={index}
          d={pathData}
          fill={colors[index % colors.length]}
          stroke="#fff"
          strokeWidth="2"
          onMouseEnter={(e) => {
            e.target.setAttribute('stroke', '#000');
            e.target.setAttribute('strokeWidth', '3');
            
            // Create tooltip
            let tooltip = document.getElementById('pie-tooltip');
            if (!tooltip) {
              tooltip = document.createElement('div');
              tooltip.id = 'pie-tooltip';
              tooltip.className = 'fixed bg-black text-white text-xs rounded py-2 px-3 pointer-events-none z-50 opacity-0 transition-opacity duration-300';
              tooltip.style.transform = 'translate(-50%, -100%)';
              tooltip.style.whiteSpace = 'nowrap';
              document.body.appendChild(tooltip);
            }
            
            tooltip.innerHTML = `
              <div>${item.name}</div>
              <div>${item.amount.toFixed(2)} kg</div>
              <div>(${percentage.toFixed(1)}%)</div>
            `;
            
            // Show tooltip with fade in
            tooltip.style.opacity = '1';
            tooltip.style.left = e.clientX + 'px';
            tooltip.style.top = (e.clientY - 30) + 'px';
            
            // Store mouse move handler
            document.pieChartMouseMoveHandler = (event) => {
              tooltip.style.left = event.clientX + 'px';
              tooltip.style.top = (event.clientY - 30) + 'px';
            };
            document.addEventListener('mousemove', document.pieChartMouseMoveHandler);
          }}
          onMouseLeave={(e) => {
            e.target.setAttribute('stroke', '#fff');
            e.target.setAttribute('strokeWidth', '2');
            
            // Hide tooltip
            const tooltip = document.getElementById('pie-tooltip');
            if (tooltip) {
              tooltip.style.opacity = '0';
            }
            
            // Remove mouse move handler
            if (document.pieChartMouseMoveHandler) {
              document.removeEventListener('mousemove', document.pieChartMouseMoveHandler);
            }
          }}
        />
      );
    });
    
    return (
      <svg width="400" height="400" viewBox="0 0 400 400" className="mx-auto">
        {paths}
        <text x="200" y="200" textAnchor="middle" dominantBaseline="middle" className="text-base font-bold fill-white">
          {total.toFixed(2)} kg
        </text>
      </svg>
    );
  };
  
  // Function to create bar chart SVG
  const createBarChart = (data, colors) => {
    if (!data || data.length === 0) return null;
    
    const maxValue = Math.max(...data.map(item => item.amount));
    // Calculate chart width based on data to ensure enough space for all bars
    const barWidth = 50; // Fixed bar width for readability
    const barSpacing = 8; // Fixed spacing for readability
    const chartWidth = Math.max(1000, data.length * (barWidth + barSpacing)); // Make wider for more data
    const chartHeight = 600;
    
    // Rotate text if there are many items or names are long
    const shouldRotateLabels = data.length > 8 || data.some(d => d.name.length > 10);
    
    const bars = data.map((item, index) => {
      const x = index * (barWidth + barSpacing) + barSpacing / 2;
      const height = (item.amount / maxValue) * (chartHeight - 100); // Reduced space for labels
      const y = chartHeight - height - 70; // Adjusted space for labels
      
      return (
        <g key={index}>
          <rect
            x={x}
            y={y}
            width={barWidth}
            height={height}
            fill={colors[index % colors.length]}
            rx="4"
            onMouseEnter={(e) => {
              e.target.setAttribute('fill', '#000');
              
              // Create tooltip
              let tooltip = document.getElementById('bar-tooltip');
              if (!tooltip) {
                tooltip = document.createElement('div');
                tooltip.id = 'bar-tooltip';
                tooltip.className = 'fixed bg-black text-white text-xs rounded py-2 px-3 pointer-events-none z-50 opacity-0 transition-opacity duration-300';
                tooltip.style.transform = 'translate(-50%, -100%)';
                tooltip.style.whiteSpace = 'nowrap';
                document.body.appendChild(tooltip);
              }
              
              tooltip.innerHTML = `
                <div>${item.name}</div>
                <div>${item.amount.toFixed(2)} kg</div>
              `;
              
              // Show tooltip with fade in
              tooltip.style.opacity = '1';
              tooltip.style.left = (e.clientX) + 'px';
              tooltip.style.top = (e.clientY - 30) + 'px';
              
              // Store mouse move handler
              document.barChartMouseMoveHandler = (event) => {
                tooltip.style.left = event.clientX + 'px';
                tooltip.style.top = (event.clientY - 30) + 'px';
              };
              document.addEventListener('mousemove', document.barChartMouseMoveHandler);
            }}
            onMouseLeave={(e) => {
              e.target.setAttribute('fill', colors[index % colors.length]);
              
              // Hide tooltip
              const tooltip = document.getElementById('bar-tooltip');
              if (tooltip) {
                tooltip.style.opacity = '0';
              }
              
              // Remove mouse move handler
              if (document.barChartMouseMoveHandler) {
                document.removeEventListener('mousemove', document.barChartMouseMoveHandler);
              }
            }}
          />
          <text
            x={x + barWidth / 2}
            y={chartHeight - 10}
            textAnchor="left"
            className="text-xs fill-current text-gray-600"
            transform={shouldRotateLabels ? `rotate(-45, ${x + barWidth / 2}, ${chartHeight})` : `translate(${x + barWidth / 2}, ${chartHeight - 1})`}
          >
            {item.name}
          </text>
        </g>
      );
    });
    
    return (
      <svg width={chartWidth} height={chartHeight} className="mx-auto">
        <g transform="translate(25, 10)">
          {bars}
        </g>
      </svg>
    );
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb />

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="mt-1 text-sm text-gray-600">
          Detailed analytics and data visualizations
        </p>
      </div>

      {/* Ocean Waste Collection Breakdown */}
      {/* <div className="grid grid-cols-1 gap-6">
        <Card title="Ocean Waste Collection Breakdown" subtitle="Types of waste collected from ocean">
          <div className="h-96">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">Loading ocean waste data...</p>
              </div>
            ) : error ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-red-500">Error loading data: {error}</p>
              </div>
            ) : oceanWasteData ? (
              <OceanWasteChart data={oceanWasteData} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">No ocean waste data available</p>
              </div>
            )}
          </div>
        </Card>
      </div> */}

      {/* Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Center</label>
          <select
            value={selectedCenter}
            onChange={(e) => setSelectedCenter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Centers</option>
            {uniqueCenters.map(center => (
              <option key={center} value={center}>{center}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Waste Type</label>
          <select
            value={selectedWasteType}
            onChange={(e) => setSelectedWasteType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            {uniqueWasteTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Waste Name</label>
          <select
            value={selectedWasteName}
            onChange={(e) => setSelectedWasteName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Names</option>
            {uniqueWasteNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Waste by Center - Original Visualization */}
      <div className="grid grid-cols-1 gap-6">
        <Card title="Waste by Center" subtitle="Distribution of waste across centers">
          <div className="h-96">
            {chartData && chartData.byCenter && chartData.byCenter.length > 0 ? (
              <div className="h-full flex flex-col">
                <div className="flex-1 space-y-2 overflow-y-auto max-h-80">
                  {chartData.byCenter.map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-64 text-sm text-gray-600 truncate">{item.name}</div>
                      <div className="flex-1 ml-2">
                        <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${chartData.byCenter.length > 0 ? Math.min(100, (item.amount / Math.max(...chartData.byCenter.map(x => x.amount))) * 100) : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="w-20 text-right text-sm font-medium text-gray-900">{item.amount.toFixed(2)} kg</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        </Card>
      </div>
      
      {/* Waste by Center - Bar Chart Visualization */}
      <div className="grid grid-cols-1 gap-6">
        <Card title="Waste by Center" subtitle="Bar chart visualization">
          <div className="h-full overflow-x-auto">
            <div className="min-w-max">
              {chartData && chartData.byCenter && chartData.byCenter.length > 0 ? (
                createBarChart(chartData.byCenter, ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'])
              ) : (
                <div className="text-center">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Waste by Type Visualization */}
      <div className="grid grid-cols-1 gap-6">
        <Card title="Waste by Type" subtitle="Distribution of waste by type">
          <div className="h-96">
            {chartData && chartData.byType && chartData.byType.length > 0 ? (
              <div className="h-full flex flex-col">
                <div className="flex-1 space-y-2 overflow-y-auto max-h-80">
                  {chartData.byType.map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-64 text-sm text-gray-600 truncate">{item.name}</div>
                      <div className="flex-1 ml-2">
                        <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full" 
                            style={{ width: `${chartData.byType.length > 0 ? Math.min(100, (item.amount / Math.max(...chartData.byType.map(x => x.amount))) * 100) : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="w-20 text-right text-sm font-medium text-gray-900">{item.amount.toFixed(2)} kg</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        </Card>
      </div>
      
      {/* Waste by Type - Bar Chart Visualization */}
      <div className="grid grid-cols-1 gap-6">
        <Card title="Waste by Type" subtitle="Bar chart visualization">
          <div className="h-full overflow-x-auto">
            <div className="min-w-max">
              {chartData && chartData.byType && chartData.byType.length > 0 ? (
                createBarChart(chartData.byType, ['#10B981', '#EF4444', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'])
              ) : (
                <div className="text-center">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
      
      {/* Waste by Name Visualization */}
      <div className="grid grid-cols-1 gap-6">
        <Card title="Waste by Name" subtitle="Distribution of waste by name">
          <div className="h-96">
            {chartData && chartData.byName && chartData.byName.length > 0 ? (
              <div className="h-full flex flex-col">
                <div className="flex-1 space-y-2 overflow-y-auto max-h-80">
                  {chartData.byName.map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-64 text-sm text-gray-600 truncate">{item.name}</div>
                      <div className="flex-1 ml-2">
                        <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-purple-500 rounded-full" 
                            style={{ width: `${chartData.byName.length > 0 ? Math.min(100, (item.amount / Math.max(...chartData.byName.map(x => x.amount))) * 100) : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="w-20 text-right text-sm font-medium text-gray-900">{item.amount.toFixed(2)} kg</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        </Card>
      </div>
      
      {/* Waste by Name - Bar Chart Visualization */}
      <div className="grid grid-cols-1 gap-6">
        <Card title="Waste by Name" subtitle="Bar chart visualization">
          <div className="h-full overflow-x-auto">
            <div className="min-w-max">
              {chartData && chartData.byName && chartData.byName.length > 0 ? (
                createBarChart(chartData.byName, ['#8B5CF6', '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'])
              ) : (
                <div className="text-center">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>



      {/* Download CSV Section */}
      <div className="grid grid-cols-1 gap-6">
        <Card title="Export Data" subtitle="Download detailed waste data as CSV file">
          <div className="flex justify-center py-8">
            <button 
              onClick={() => downloadCSV(filteredData)}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
              disabled={filteredData.length === 0}
            >
              <Download className="h-5 w-5 mr-2" />
              Download Detailed Data (CSV)
            </button>
          </div>
          {filteredData.length === 0 && (
            <p className="text-center text-gray-500 text-sm">No data available to download</p>
          )}
        </Card>
      </div>

      {/* Other Chart Placeholders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Revenue Analytics" subtitle="Revenue trends and forecasts">
          <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">D3.js Revenue Chart</p>
              <p className="text-sm text-gray-400">Advanced revenue visualization</p>
            </div>
          </div>
        </Card>

        <Card title="Geographic Distribution" subtitle="User distribution by location">
          <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="h-16 w-16 bg-gray-300 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <span className="text-gray-500 text-xs">MAP</span>
              </div>
              <p className="text-gray-500 font-medium">D3.js Geographic Chart</p>
              <p className="text-sm text-gray-400">World map with user data</p>
            </div>
          </div>
        </Card>

        <Card title="User Activity Heatmap" subtitle="Activity patterns over time">
          <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <Activity className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">D3.js Activity Heatmap</p>
              <p className="text-sm text-gray-400">Calendar-style heatmap</p>
            </div>
          </div>
        </Card>

        <Card title="Performance Metrics" subtitle="KPI gauges and indicators">
          <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <PieChart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">D3.js Gauge Charts</p>
              <p className="text-sm text-gray-400">Performance indicators</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;